import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import type { CommunityRow } from '../outreachTypes';
import { compactCount } from '../outreachFormat';
import { SelfPromoChip } from './SelfPromoChip';
import { communityMessages as messages } from './communityMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Communities.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CommunityRowViewProps {
  row: CommunityRow;
}

/**
 * One community (design outreach r1): the handle and description, the member count, the posting
 * rules, the self-promo chip and a link out to the subreddit. Read only: no pick, no pitch.
 */
const CommunityRowViewComponent: React.FC<CommunityRowViewProps> = ({ row }) => {
  const intl = useIntl();
  const description = row.genres.length > 0 ? row.genres.join(' · ') : row.platform;

  return (
    <div className={clsx(shared.tableRow, styles.communityRow)} data-testid="community-row">
      <span className={shared.cover} />
      <span className={shared.cellTitle}>
        <b>{row.handle}</b>
        <small>{description}</small>
      </span>
      <span className={clsx(shared.cellNum, styles.hideNarrow)}>
        {compactCount(intl, row.members)}
        <small>{intl.formatMessage(messages.membersLabel)}</small>
      </span>
      <span className={clsx(styles.rules, styles.hideNarrow)}>{row.postingRules}</span>
      <span className={styles.hideNarrow}>
        <SelfPromoChip allowsSelfPromo={row.allowsSelfPromo} dayOrThread={row.dayOrThread} />
      </span>
      <span className="d-flex justify-content-end">
        <a
          className={styles.openLink}
          href={row.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={intl.formatMessage(messages.open, { name: row.handle })}
          onClick={(event) => event.stopPropagation()}
        >
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
        </a>
      </span>
    </div>
  );
};

export const CommunityRowView = TypedMemo(CommunityRowViewComponent);
